import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import CanvasContext from '../context/CanvasContext';
import * as CanvasAPI from '../services/CanvasServices';
import * as ScenarioAPI from '../services/ScenarioServices';
import * as AssignmentAPI from '../services/AssignmentServices';

/**
 * useReadOnlyCanvas — the graph for everything that looks at a scenario without
 * editing it: the heat map, the classroom progress view, the admin viewer.
 *
 * These used to mount the full editing provider, which meant they opened
 * collaboration sockets, claimed the scenario for editing, and (worst) rewrote
 * node positions when a node arrived without one — a viewer silently scrambling
 * the author's layout. None of that happens here: this loads the graph, holds a
 * selection, and stops.
 *
 * It asks for the draft first and falls back to the published map when the server
 * says this user may not have it. That is the right answer in both directions: an
 * owner inspecting their own heat map wants their current graph, and a teacher
 * reviewing a classroom is not the scenario's owner and wants the version their
 * students actually played.
 */

/**
 * The draft is owner/collaborator-only. A teacher or admin looking at somebody
 * else's scenario gets a 403 here, which is not an error — it is the signal to
 * read the published snapshot instead.
 */
async function loadGraph(scenarioId) {
    try {
        return await CanvasAPI.getCanvas(scenarioId);
    } catch (error) {
        const status = error?.response?.status;
        if (status !== 401 && status !== 403) throw error;
        return CanvasAPI.getPublishedMap(scenarioId);
    }
}
export default function useReadOnlyCanvas({ scenarioId, withHeatmap = false } = {}) {
    const [scenario, setScenario] = useState(null);
    const [graph, setGraph] = useState({ nodes: [], edges: [] });
    const [loading, setLoading] = useState(true);

    // Selection here is purely local — no awareness, no socket, nobody to tell.
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [selectedEdgeId, setSelectedEdgeId] = useState(null);

    const [heatmap, setHeatmap] = useState({
        nodes: [],
        edges: [],
        nodeUsers: [],
        edgeUsers: [],
    });

    useEffect(() => {
        if (!scenarioId) return undefined;

        let cancelled = false;
        setLoading(true);

        (async () => {
            try {
                const [fetchedScenario, canvas] = await Promise.all([
                    ScenarioAPI.getScenario(scenarioId),
                    loadGraph(scenarioId),
                ]);
                if (cancelled) return;

                setScenario(fetchedScenario);
                setGraph({ nodes: canvas?.nodes || [], edges: canvas?.edges || [] });
            } catch (error) {
                if (!cancelled) console.error('Error loading canvas for viewing:', error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [scenarioId]);

    const getHeatmapInfo = useCallback(async () => {
        if (!scenarioId) return;
        try {
            const data = await CanvasAPI.getHeatmap(scenarioId);
            setHeatmap({
                nodes: data.nodes || [],
                edges: data.edges || [],
                nodeUsers: data.nodeUsers || [],
                edgeUsers: data.edgeUsers || [],
            });
        } catch (error) {
            console.error('Error fetching heat map:', error);
        }
    }, [scenarioId]);

    useEffect(() => {
        if (withHeatmap) getHeatmapInfo();
    }, [withHeatmap, getHeatmapInfo]);

    const getNode = useCallback(
        (nodeId) => graph.nodes.find((node) => node.id === nodeId) || null,
        [graph.nodes]
    );
    const getEdge = useCallback(
        (edgeId) => graph.edges.find((edge) => edge.id === edgeId) || null,
        [graph.edges]
    );

    const selectNode = useCallback((nodeId) => {
        setSelectedNodeId(nodeId);
        setSelectedEdgeId(null);
    }, []);
    const selectEdge = useCallback((edgeId) => {
        setSelectedNodeId(null);
        setSelectedEdgeId(edgeId);
    }, []);
    const clearSelection = useCallback(() => {
        setSelectedNodeId(null);
        setSelectedEdgeId(null);
    }, []);

    return useMemo(
        () => ({
            scenario,
            nodes: graph.nodes,
            edges: graph.edges,
            loading,
            selectedNodeId,
            selectedEdgeId,
            selectNode,
            selectEdge,
            clearSelection,
            getNode,
            getEdge,
            heatmapNodes: heatmap.nodes,
            heatmapEdges: heatmap.edges,
            heatmapNodeUsers: heatmap.nodeUsers,
            heatmapEdgeUsers: heatmap.edgeUsers,
            getHeatmapInfo,
        }),
        [
            scenario,
            graph.nodes,
            graph.edges,
            loading,
            selectedNodeId,
            selectedEdgeId,
            selectNode,
            selectEdge,
            clearSelection,
            getNode,
            getEdge,
            heatmap,
            getHeatmapInfo,
        ]
    );
}

/**
 * Supplies the read-only graph through CanvasContext so the viewer components
 * below (canvases, nodes, edges, sidebars) keep reading from the one place they
 * always have. The value is a strict subset of the editing context: anything
 * that would write is simply absent, which is the point.
 */
export const ReadOnlyCanvasProvider = ({ withHeatmap = false, children }) => {
    const { scenarioId, assignmentId } = useParams();
    const readOnly = useReadOnlyCanvas({ scenarioId, withHeatmap });

    // --- Classroom review extras -------------------------------------------
    const [assignmentWithSubmissions, setAssignmentWithSubmissions] = useState(null);
    const [adminSelectedNode, setAdminSelectedNode] = useState(null);
    const [adminSelectedEdge, setAdminSelectedEdge] = useState(null);

    /**
     * Which student path is currently traced on the canvas, as `{ ids, color }`.
     *
     * Highlighting used to work by rewriting every node and edge in place and
     * restoring them from an `originalNodes` array that was never populated — so
     * clearing a highlight blanked the canvas. It is derived state now: the
     * loaded graph is never touched, and clearing the highlight is just dropping
     * this.
     */
    const [highlight, setHighlight] = useState(null);

    const fetchAssignmentSubmissions = useCallback(async () => {
        if (!assignmentId) return;
        try {
            const data = await AssignmentAPI.getAssignmentWithSubmissions(assignmentId);
            setAssignmentWithSubmissions(data);
        } catch (error) {
            console.error('Error fetching assignment submissions:', error);
        }
    }, [assignmentId]);

    /** Traces one submission's path across the graph in that student's colour. */
    const highlightSubmission = useCallback((submission) => {
        if (!submission?.decisions) return;
        setHighlight({
            // The API flattens decisions to `{ _id, type, title }`. The old code
            // read `decision.id`, which is never present, so no path was ever
            // actually drawn.
            ids: new Set(submission.decisions.map((d) => String(d._id ?? d.id))),
            color: submission.color || '#FFFFFF',
            by: submission._id,
        });
    }, []);

    /** Same, for a student's log rather than an assignment submission. */
    const highlightStudentLog = useCallback((studentId, log) => {
        const ids = [
            ...(log?.nodes || []).map((d) => String(d._id ?? d.id)),
            ...(log?.edges || []).map((d) => String(d._id ?? d.id)),
        ];
        setHighlight({ ids: new Set(ids), color: log?.color || '#FFFFFF', by: studentId });
    }, []);

    const resetHighlightSubmission = useCallback(() => setHighlight(null), []);

    // The highlight is painted on the way out, so the loaded graph stays pristine
    // and clearing it cannot lose anything.
    const decorated = useMemo(() => {
        if (!highlight) return { nodes: readOnly.nodes, edges: readOnly.edges };

        const paint = (item, colorField) =>
            highlight.ids.has(item.id)
                ? {
                      ...item,
                      data: { ...item.data, [colorField]: highlight.color, highlightedBy: highlight.by },
                  }
                : item;

        return {
            nodes: readOnly.nodes.map((node) => paint(node, 'fillColor')),
            edges: readOnly.edges.map((edge) => paint(edge, 'edgeColor')),
        };
    }, [highlight, readOnly.nodes, readOnly.edges]);

    const value = useMemo(
        () => ({
            ...readOnly,
            nodes: decorated.nodes,
            edges: decorated.edges,

            // Selection handlers, under the names the viewer components use.
            onSelectNode: readOnly.selectNode,
            onSelectEdge: readOnly.selectEdge,
            onCanvasClick: readOnly.clearSelection,
            onSelectNodeClassroom: readOnly.selectNode,
            onSelectEdgeClassroom: readOnly.selectEdge,
            onCanvasClickClassroom: readOnly.clearSelection,

            onSelectNodeAdmin: (node) => {
                setAdminSelectedNode(node);
                setAdminSelectedEdge(null);
            },
            onSelectEdgeAdmin: (edge) => {
                setAdminSelectedNode(null);
                setAdminSelectedEdge(edge);
            },
            onCanvasClickAdmin: () => {
                setAdminSelectedNode(null);
                setAdminSelectedEdge(null);
            },
            adminSelectedNode,
            adminSelectedEdge,
            setAdminSelectedNode,
            setAdminSelectedEdge,

            assignmentWithSubmissions,
            setAssignmentWithSubmissions,
            fetchAssignmentSubmissions,
            highlightSubmission,
            highlightStudentLog,
            resetHighlightSubmission,
        }),
        [
            readOnly,
            decorated,
            adminSelectedNode,
            adminSelectedEdge,
            assignmentWithSubmissions,
            fetchAssignmentSubmissions,
            highlightSubmission,
            highlightStudentLog,
            resetHighlightSubmission,
        ]
    );

    return <CanvasContext.Provider value={value}>{children}</CanvasContext.Provider>;
};
